import { useEffect, useState } from "react"
import useTasksFiltersFormik from "../services/tasks/hooks/use-tasks-filters-formik"
import createSimpleContext from "../utils/hooks/create-simple-context"
import { useMutation, useQueryClient } from "react-query"
import { useApiClient } from "../services/api-client/api-client.hooks"
import { TApiProject, TApiProjectSection, TApiTaskDetails } from "../services/api-client/types"
import { cloneDeep, uniqBy } from "lodash"
import { useFormik } from "formik"
import useCreateTaskMutation from "../services/tasks/queries/use-create-task-mutation"
import { useGetProject } from "../services/projects/queries/use-get-project"
import { TApiTasksFilters } from "../services/api-client/tasks-api"

interface useProjectBoardProps {
  onFilteredTaskCreated?(): void
}

const useProjectBoardFeature = (props: useProjectBoardProps) => {
  const tasksFiltersFormik = useTasksFiltersFormik({
    onSubmit: () => {
    }
  })

  const [isDraggingSection, setIsDraggingSection] = useState(false)

  const [newTaskFormVisible, setNewTaskFormVisible] = useState(false)

  const newTaskFormik = useFormik<Partial<TApiTaskDetails>>({
    initialValues: {},
    enableReinitialize: true,
    onSubmit: (values, formikHelper) => {
      createTaskMutation.mutate(values)
    },
  })

  const createTaskMutation = useCreateTaskMutation({
    projectId: `${newTaskFormik.values.projectId}`,
    tasksFilters: tasksFiltersFormik.values,
  }, {
    onMutate: () => {
      newTaskFormik.setSubmitting(true)
    },
    onError: (errors) => {
      newTaskFormik.setErrors(errors)
    },
    onSuccess: () => {
      const areFiltersApplied = tasksFiltersFormik.dirty
      newTaskFormik.resetForm()

      if (props.onFilteredTaskCreated && areFiltersApplied) props.onFilteredTaskCreated()
    },
    onSettled: () => {
      newTaskFormik.setSubmitting(false)
    }
  })

  const showNewTaskForm = (values?: Partial<TApiTaskDetails>) => {
    newTaskFormik.setValues({
      ...newTaskFormik.values,
      ...values
    })
    setNewTaskFormVisible(true)
  }

  const hideNewTaskForm = () => {
    newTaskFormik.resetForm()
    setNewTaskFormVisible(false)
  }

  // Isolate formik values from feature state
  const [tasksFilters, setTasksFilters] = useState(tasksFiltersFormik.values)

  useEffect(() => {
    if (tasksFiltersFormik.dirty) {
      setTasksFilters(tasksFiltersFormik.values)
    }
  }, [JSON.stringify(tasksFiltersFormik.values)])

  return {
    isDraggingSection,
    setIsDraggingSection,
    tasksFilters,
    setTasksFilters,
    tasksFiltersFormik,

    newTaskFormVisible,
    showNewTaskForm,
    hideNewTaskForm,
    newTaskFormik,
  }
}

interface UseTaskFormikProps {
  initialValues: Partial<TApiTaskDetails>
}

export const useTaskFormik = (props?: UseTaskFormikProps) => {
  const initialValues = props?.initialValues || {}

  const formik = useFormik<Partial<TApiTaskDetails>>({
    initialValues: initialValues,
    enableReinitialize: true,
    onSubmit: (values, formikHelper) => {
      //window.alert(JSON.stringify(initialValues))
      //window.alert(JSON.stringify(values))
      createTaskMutation.mutate(values)
    },
  })

  const createTaskMutation = useCreateTaskMutation({
    projectId: `${formik.values.projectId}`
  }, {
    onMutate: () => {
      formik.setSubmitting(true)
    },
    onError: (errors) => {
      formik.setErrors(errors)
    },
    onSuccess: () => {
      formik.resetForm()
    },
    onSettled: () => {
      formik.setSubmitting(false)
    }
  })

  return formik
}

export const {
  useValue: useProjectBoard,
  Provider: ProjectBoardProvider,
  withProvider: withProjectBoard,
} = createSimpleContext(useProjectBoardFeature)

interface UseLoadProjectSectionTasksProps {
  projectId: string
  projectSectionId?: string
}

interface Args {
  projectSectionId?: string
}

const PAGE_SIZE = 10

export const useLoadProjectSectionTasks = (props: UseLoadProjectSectionTasksProps) => {
  const apiClient = useApiClient()
  const queryClient = useQueryClient()

  const [limit, setLimit] = useState(PAGE_SIZE)

  const {
    tasksFiltersFormik
  } = useProjectBoard()

  const filters = tasksFiltersFormik.values

  const getSectionTasksData = (sectionId: string) => {
    const data = queryClient.getQueryData(["project-sections", props.projectId, filters]) as any
    const section = data.projectSections.find((ps) => ps.id == sectionId) as TApiProjectSection

    return {
      tasks: section.tasks.tasks,
      totalCount: section.tasks.meta.pagination.totalCount,
    }
  } 

  // TODO: fix strong assumption project is always downlaoded at this point!
  const project = useGetProject({ projectId: props.projectId })

  const mutation = useMutation(
    ['project-section-tasks-load'], 
    async (args?: Args) => {
      const projectSectionId = args.projectSectionId || props.projectSectionId
      if (!projectSectionId) throw 'Project section id not provided to load more tasks mutation!'

      const {
        tasks,
        totalCount,
      } = getSectionTasksData(projectSectionId)

      const reqFilters = cloneDeep(filters)

      if (reqFilters.users !== undefined && reqFilters.users.length > 0) {
        const userStrings = reqFilters.users.map((userHashId) => {
          if (userHashId == 'no_assignee') return 'no_assignee'
          return `${userHashId}|UsersAccount`
        })
        reqFilters.assignees = userStrings
        delete reqFilters.users
      }

      if (reqFilters.groups !== undefined && reqFilters.groups.length > 0) {
        const groupSrings = reqFilters.groups.map((groupHashId) => {
          return `${groupHashId}|MembersGroup`
        })

        if (!reqFilters.assignees) {
          reqFilters.assignees = []
        }

        reqFilters.assignees = [...reqFilters.assignees, ...groupSrings]
        delete reqFilters.groups
      }

      const response = await apiClient.get(`/projects/${props.projectId}/project_sections/${projectSectionId}/tasks/project_section_list`, {
        // q: { s: ["project_section_position ASC"] },
        limit: PAGE_SIZE,
        offset: tasks.length,
        filters: reqFilters 
      })

      if (!response.ok) throw response.errors

      return {
        tasks: response.tasks.map((task) => ({
          ...task,
          project: project,
        })),
        tasksCount: response.meta.pagination.totalCount,
      }
    },
    {
      onSuccess: (data, args) => {
        queryClient.setQueryData(["project-sections", props.projectId, filters], (oldData) => {
          const projectSectionId = args.projectSectionId || props.projectSectionId

          const newData = cloneDeep(oldData)

          const section = newData.projectSections.find((ps) => ps.id == projectSectionId) as TApiProjectSection
          section.tasks.tasks = [...section.tasks.tasks, ...data.tasks]
          
          // TODO: remove after backend fix
          section.tasks.tasks = uniqBy(section.tasks.tasks, (t) => t.id)
          section.tasks.meta.pagination.totalCount = data.tasksCount

          return newData
        })
      }
    }
  )

  return mutation
}


interface ProjectSectionsData {
  projectSections: TApiProjectSection[]
}

export const useRefetchProjectSectionTasksAfterInjection = (props: {
  projectId: string
  filters?: TApiTasksFilters
}) => {
  const apiClient = useApiClient()
  const queryClient = useQueryClient()

  const project = useGetProject({
    projectId: props.projectId
  })

  const mutation = useMutation(
    async (projectSectionId: string) => {
      // Get current tasks length
      const currentData = queryClient.getQueryData(["project-sections", props.projectId, props.filters]) as ProjectSectionsData
      const currentSection = currentData?.projectSections?.find((ps) => ps.id === projectSectionId)
      const currentTasksLength = currentSection?.tasks?.tasks?.length || 0

      let reqFilters = { ...props.filters }

      // Handle groups to assignees conversion exactly as in reference
      if (reqFilters?.groups?.length > 0) {
        const groupSrings = reqFilters.groups.map((g) => g.toString())

        if (!reqFilters.assignees) {
          reqFilters.assignees = []
        }

        reqFilters.assignees = [...reqFilters.assignees, ...groupSrings]
        delete reqFilters.groups
      }

      const response = await apiClient.get(`/projects/${props.projectId}/project_sections/${projectSectionId}/tasks/project_section_list`, {
        // q: { s: ["project_section_position ASC"] },
        limit: currentTasksLength + 1,
        offset: 0,
        filters: reqFilters
      })

      if (!response.ok) throw response.errors

      return {
        tasks: response.tasks.map((task) => ({
          ...task,
          project: project,
        })),
        tasksCount: response.meta.pagination.totalCount,
      }
    },
    {
      onSuccess: (data, args) => {
        queryClient.setQueryData(["project-sections", props.projectId, props.filters], (oldData: ProjectSectionsData) => {
          const projectSectionId = args
          const newData = cloneDeep(oldData)

          const section = newData.projectSections.find((ps) => ps.id == projectSectionId) as TApiProjectSection
          section.tasks.tasks = data.tasks
          section.tasks.meta.pagination.totalCount = data.tasksCount

          return newData
        })
      }
    }
  )

  return mutation
}
